import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useRazorpay } from 'react-razorpay';

import * as planApis from 'apis/plans';
import * as subscriptionApis from 'apis/subscriptions';
import plan1 from '../../../assets/images/plan1.jpg';
import plan2 from '../../../assets/images/plan2.jpg';
import plan3 from '../../../assets/images/plan3.jpg';
import plan4 from '../../../assets/images/plan4.jpg';
import plan5 from '../../../assets/images/plan5.jpg';
import './MySubscription.scss';

/*
  Subscription page, converted from the live-test.html mockup.

  The live page used the old light Pricing cards (Pricing/Large.js + Mini.js).
  The tiles here are the same cards as the public /Plans page
  (PlansListStatic) - photo only behind the icon/name/price zone, features
  and action on a plain black body - with two dashboard extras: a
  current-plan strip from GET /v1/subscriptions/user, and Switch / Current
  Plan instead of Subscribe Now.

  Change-plan and cancel still hit the same endpoints Pricing used:
  POST /v1/subscriptions/change-plan and /cancel. UPI still opens Razorpay
  on a new subscription; card still updates the existing one. The legacy
  page is kept, unrouted, in MySubscription.backup.js.
*/

const PLAN_ICONS = [
  'fa-solid fa-fire',
  'fa-solid fa-fire-flame-curved',
  'fa-solid fa-gem',
  'fa-solid fa-crown',
];

const PLAN_IMAGES = [plan5, plan4, plan3, plan2, plan1];

const CYCLE_LABEL = { monthly: 'mo', yearly: 'yr' };

const iconForRank = (index) => PLAN_ICONS[Math.min(index, PLAN_ICONS.length - 1)];
const imageForRank = (index) => PLAN_IMAGES[Math.min(index, PLAN_IMAGES.length - 1)];

const cycleLabel = (cycle) => CYCLE_LABEL[cycle?.toLowerCase()] || cycle || 'mo';

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (date) =>
  date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const paymentLabel = (method) => {
  if (!method) return '—';
  if (method.toLowerCase() === 'upi') return 'UPI';
  if (method.toLowerCase() === 'card') return 'Card';
  return method;
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ConfirmModal = ({
  title,
  body,
  confirmLabel,
  danger,
  busy,
  onKeep,
  onConfirm,
}) => (
  <div
    className="sub-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="sub-modal-title"
    onClick={busy ? undefined : onKeep}
  >
    <div className="sub-modal-card" onClick={(event) => event.stopPropagation()}>
      <h3 id="sub-modal-title">{title}</h3>
      <p>{body}</p>
      <div className="sub-modal-actions">
        <button type="button" className="sub-btn-ghost" onClick={onKeep} disabled={busy}>
          Keep plan
        </button>
        <button
          type="button"
          className={`sub-btn-primary${danger ? ' is-danger' : ''}`}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Please wait…' : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

function MySubscription() {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [freePeriodEligible, setFreePeriodEligible] = useState(false);

  const { Razorpay } = useRazorpay();

  const load = useCallback(async () => {
    try {
      const [plansResponse, subscriptionResponse, eligibilityResponse] = await Promise.allSettled([
        planApis.fetchPlans(),
        subscriptionApis.fetchSubscription(),
        subscriptionApis.fetchFreePeriodEligibility(),
      ]);

      if (plansResponse.status === 'fulfilled') {
        setPlans(plansResponse.value.data || []);
      }

      setFreePeriodEligible(
        eligibilityResponse.status === 'fulfilled' && Boolean(eligibilityResponse.value.data?.eligible)
      );

      if (subscriptionResponse.status === 'fulfilled') {
        const next = subscriptionResponse.value.data;
        setSubscription(next || null);
      } else {
        setSubscription(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visiblePlans = useMemo(
    () =>
      plans
        .slice()
        .sort((a, b) => Number(a.price) - Number(b.price)),
    [plans]
  );

  const isSubscriber = subscription?.status === 'active';
  const currentPlanId = subscription?.plan?.id;
  const isUpi = subscription?.paymentMethod === 'upi';
  const currentPlanRank = visiblePlans.findIndex((plan) => plan.id === currentPlanId);

  const closeConfirm = () => {
    if (busy) return;
    setConfirm(null);
    setPendingPlan(null);
  };

  useEffect(() => {
    if (!confirm) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !busy) {
        setConfirm(null);
        setPendingPlan(null);
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('no-scroll');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('no-scroll');
    };
  }, [confirm, busy]);

  const waitForActivePlan = async (planId) => {
    const maxAttempts = 15;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const { data } = await subscriptionApis.fetchSubscription();
      if (Number(data?.plan?.id) === Number(planId)) {
        setSubscription(data || null);
        return true;
      }
      await delay(2000);
    }
    await load();
    return false;
  };

  const handlePayment = ({ razorpayKey, razorpaySubscriptionId: subscriptionId, email, phone, planId }) => {
    const options = {
      key: razorpayKey,
      name: 'The Taurian',
      description: 'Subscription Payment',
      subscription_id: subscriptionId,
      image: '/your_logo.jpg',
      handler: async (response) => {
        const {
          razorpay_payment_id: paymentId,
          razorpay_subscription_id: verifiedSubscriptionId,
          razorpay_signature: signature,
        } = response;
        try {
          let shouldWait = true;
          let attempts = 0;
          while (shouldWait && attempts < 15) {
            attempts += 1;
            const verificationResponse = await subscriptionApis.verify({
              paymentId,
              subscriptionId: verifiedSubscriptionId,
              signature,
            });
            if (!verificationResponse.data?.wait) {
              shouldWait = false;
            } else {
              await delay(2000);
            }
          }
          if (shouldWait) {
            throw new Error('Payment verification timed out');
          }
          if (planId) {
            const switched = await waitForActivePlan(planId);
            if (switched) {
              toast.success('Plan changed successfully.');
            } else {
              toast.success('Payment received. Your new plan will show here once the switch is confirmed.');
            }
          } else {
            await load();
          }
        } catch (error) {
          toast.error('Could not verify the payment. Please try again.');
        }
      },
      prefill: {
        email,
        contact: phone,
      },
      theme: {
        color: '#F37254',
      },
      modal: {
        ondismiss: function () {
          subscriptionApis.updatChangePlanStatus({
            razorpaySubscriptionId: subscriptionId,
            manualUpdateStatus: 'payment_closed',
          });
        },
      },
    };

    const razorpayInstance = new Razorpay(options);
    razorpayInstance.open();
    razorpayInstance.on('payment.failed', function () {
      toast.error('Your Payment Failed, Try again');
    });
  };

  const handlePlanChangeConfirmation = async () => {
    if (!pendingPlan) return;
    setBusy(true);
    try {
      const { data: updatedSubscription } = await subscriptionApis.changePlan(pendingPlan.id);
      if (updatedSubscription?.razorpayKey) {
        subscriptionApis.updatChangePlanStatus({
          razorpaySubscriptionId: updatedSubscription.razorpaySubscriptionId,
          manualUpdateStatus: 'payment_triggered',
        });
        handlePayment({ ...updatedSubscription, planId: pendingPlan.id });
      } else {
        await load();
        toast.success('Plan changed successfully.');
      }
      setConfirm(null);
      setPendingPlan(null);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to change the plan.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancelConfirmation = async () => {
    setBusy(true);
    try {
      await subscriptionApis.cancel();
      setSubscription(null);
      setConfirm(null);
      toast.success('Subscription canceled successfully.');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to cancel the subscription.');
    } finally {
      setBusy(false);
    }
  };

  const openChangeConfirm = (plan) => {
    setPendingPlan(plan);
    setConfirm('change');
  };

  const currentCycle = cycleLabel(subscription?.plan?.billingCycle);
  const renewsOn = toDate(subscription?.currentPeriodEnd);
  const freePeriodEndsOn = toDate(subscription?.freePeriodEndsAt);
  const inFreePeriod = isSubscriber && Boolean(freePeriodEndsOn) && freePeriodEndsOn > new Date();

  return (
    <div className="sub-page">
      <div className="sub-container">

        <div className="sub-head">
          <span className="sub-eyebrow">Membership</span>
          <h1 className="sub-title">Subscription</h1>
          <p className="sub-lead">Your current plan, renewal date and the other tiers you can switch to.</p>
        </div>

        {isSubscriber && (
          <div className="sub-now">
            <div className="sub-now-top">
              <div className="pp-plan-icon-ring">
                <i className={iconForRank(currentPlanRank < 0 ? 1 : currentPlanRank)}></i>
              </div>
              <div className="sub-now-copy">
                <div className="sub-now-kicker">Current plan</div>
                <div className="sub-now-name">{subscription.plan?.name}</div>
                <div className="sub-now-price">
                  {inFreePeriod ? (
                    <>
                      Free until {formatDate(freePeriodEndsOn)}
                      <span>, then ₹{Math.trunc(subscription.plan?.price || 0)} / {currentCycle}</span>
                    </>
                  ) : (
                    <>₹{Math.trunc(subscription.plan?.price || 0)} <span>/ {currentCycle}</span></>
                  )}
                </div>
              </div>
              <span className="sub-pill"><i className="fa-solid fa-circle"></i>{inFreePeriod ? 'Free days' : 'Active'}</span>
            </div>

            <div className="sub-now-meta">
              <div>
                <span className="sub-meta-label">
                  <i className="fa-solid fa-calendar-day"></i>{inFreePeriod ? 'First charge' : 'Renews'}
                </span>
                <span className="sub-meta-value">{renewsOn ? formatDate(renewsOn) : '—'}</span>
              </div>
              <div>
                <span className="sub-meta-label"><i className="fa-solid fa-wallet"></i>Payment</span>
                <span className="sub-meta-value">{paymentLabel(subscription.paymentMethod)}</span>
              </div>
            </div>

            <div className="sub-now-foot">
              <p>Razorpay renews this plan automatically. Switch to another plan below at any time.</p>
              <button type="button" className="sub-cancel" onClick={() => setConfirm('cancel')}>
                Cancel subscription
              </button>
            </div>
          </div>
        )}

        <div className="sub-section">
          <div className="sub-section-head">
            <div className="sub-section-title">
              <i className="fa-solid fa-layer-group"></i>
              {isSubscriber ? 'Change plan' : 'Choose a plan'}
            </div>
            <span className="sub-rule"></span>
            <span className="sub-section-note">Same tiles as Membership Plans</span>
          </div>

          {loading && <div className="sub-empty">Loading plans…</div>}

          {!loading && visiblePlans.length === 0 && (
            <div className="sub-empty">No plans to show right now.</div>
          )}

          {!loading && visiblePlans.length > 0 && (
            <div className="pp-plans-list">
              {visiblePlans.map((plan, index) => {
                const isCurrent = isSubscriber && plan.id === currentPlanId;
                const isUpgrade = isSubscriber && Number(plan.price) > Number(subscription?.plan?.price);
                const cycle = cycleLabel(plan.billingCycle);
                const entries = Number(plan.noOfEntries) || 0;
                const showFreePeriod = !isSubscriber && freePeriodEligible && plan.isFreePeriodEnabled;
                const actionLabel = !isSubscriber
                  ? `Choose ${plan.name}`
                  : isUpgrade
                    ? `Upgrade to ${plan.name}`
                    : `Switch to ${plan.name}`;

                return (
                  <div className={`pp-plan-card${isCurrent ? ' is-current' : ''}`} key={plan.id}>
                    {isCurrent && <span className="pp-current-badge">Your plan</span>}

                    <div className="pp-plan-hero">
                      <div className="pp-plan-hero-bg">
                        <img src={imageForRank(index)} alt={plan.name} />
                      </div>
                      <div className="pp-plan-hero-content">
                        <div className="pp-plan-icon-ring"><i className={iconForRank(index)}></i></div>
                        <div className="pp-plan-name">{plan.name}</div>
                        <span className="pp-stripe-accent"></span>
                        <div className="pp-plan-price-row">
                          {showFreePeriod && (
                            <s className="pp-plan-price-was"><sup>₹</sup>{Math.trunc(plan.price)}</s>
                          )}
                          <div className="pp-plan-price"><sup>₹</sup>{showFreePeriod ? 0 : Math.trunc(plan.price)}</div>
                          <span className="pp-per">/{cycle}</span>
                        </div>
                        {showFreePeriod && plan.freePeriodText && (
                          <div className="pp-free-note">{plan.freePeriodText}</div>
                        )}
                      </div>
                    </div>

                    <div className="pp-plan-body">
                      <div className="pp-entries-pill">
                        <i className="fa-solid fa-ticket"></i>
                        <span className="pp-count">
                          {entries} Free {entries === 1 ? 'Entry' : 'Entries'}
                        </span>
                        <span className="pp-per-label">/ {cycle}</span>
                      </div>
                      <div className="pp-plan-divider"></div>
                      <ul className="pp-plan-features">
                        {(plan.features || []).map((feature, featureIndex) => (
                          <li key={featureIndex}>
                            <span className="pp-check-badge"><i className="fa-solid fa-check"></i></span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                      {isCurrent ? (
                        <button type="button" className="pp-subscribe-btn is-current" disabled>
                          Current Plan
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={`pp-subscribe-btn${isUpgrade ? ' is-upgrade' : isSubscriber ? ' is-switch' : ''}`}
                          onClick={() => openChangeConfirm(plan)}
                        >
                          {actionLabel}
                          <i className={`fa-solid ${isUpgrade ? 'fa-arrow-up' : 'fa-arrow-right'}`}></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {confirm === 'change' && pendingPlan && (
        <ConfirmModal
          title={`${Number(pendingPlan.price) > Number(subscription?.plan?.price) ? 'Upgrade' : 'Switch'} to ${pendingPlan.name}?`}
          body={
            inFreePeriod
              ? `Your free days will end and ₹${Math.trunc(pendingPlan.price)} will be charged now for ${pendingPlan.name}. If you close checkout, you stay on ${subscription?.plan?.name || 'your current plan'}.`
              : isUpi
              ? `UPI switches start a new subscription for ${pendingPlan.name} after you authorise payment. Any refund on your current plan is prorated. If you close checkout, you stay on ${subscription?.plan?.name || 'your current plan'}.`
              : `Are you sure you want to change your subscription to ${pendingPlan.name}?`
          }
          confirmLabel={Number(pendingPlan.price) > Number(subscription?.plan?.price) ? 'Upgrade' : 'Change plan'}
          busy={busy}
          onKeep={closeConfirm}
          onConfirm={handlePlanChangeConfirmation}
        />
      )}

      {confirm === 'cancel' && (
        <ConfirmModal
          title="Cancel subscription?"
          body="Are you sure you want to cancel your subscription?"
          confirmLabel="Cancel subscription"
          danger
          busy={busy}
          onKeep={closeConfirm}
          onConfirm={handleCancelConfirmation}
        />
      )}
    </div>
  );
}

export default MySubscription;
